<template>
  <WdsStack v-if="loading" justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsSpinner indeterminate />
      </div>
    </div>
    <WdsText large>
      {{ $t('Loading approvals...') }}
    </WdsText>
  </WdsStack>

  <div v-else-if="!loading && approvalApprovables.length > 0" class="tw-mt-6">
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
      <BaseList class="tw-col-span-8">
        <ApprovalProgress
          v-for="approvalApprovable in approvalApprovables"
          :key="`approval-approvable-${approvalApprovable.id}`"
          :approvable="approvalApprovable"
          @reload-approvals="reloadApprovables"
        />
      </BaseList>
    </div>
  </div>

  <WdsStack v-else justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsIcon name="flow" :size="20"></WdsIcon>
      </div>
    </div>
    <WdsText large>
      {{ $t('There are no existing approval') }}
    </WdsText>
    <WdsText secondary>
      {{ $t('Hires that requires approval by you or your team will be listed here') }}
    </WdsText>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsIcon, WdsText, WdsStack, WdsSpinner } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ApprovalProgress from '@/components/Workflows/Approvals/Request/ApprovalProgress.vue'
import BaseList from '@/components/BaseList.vue'
import { Approvable } from '@/components/Workflows/Approvals/approvals-types'
import { graphql } from '@/gql'
import { ApprovalApprovableOrderByColumn, SortOrder } from '@/gql/graphql'

const props = defineProps({
  hireId: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['reloadingApprovals'])

const {
  result,
  loading,
  refetch: reloadApprovalApprovables,
} = useQuery(
  graphql(`
    query ApprovalApprovables(
      $approvable: ID!
      $first: Int
      $usersFirst: Int!
      $usersPage: Int
      $orderBy: [ApprovalApprovablesOrderByClauseInput!]
    ) {
      approvalApprovables(approvable: $approvable, first: $first, orderBy: $orderBy) {
        data {
          id
          approvalRule {
            id
            approvers {
              ...ApproverGroupFragment
            }
          }
          approval {
            id
            name
            trigger
          }
          approvable {
            ... on Hire {
              # We want to use ...HireApprovableFragment here, but it uses the internalBudget field
              # which is under a feature flag. So for now we'll use the contents of that fragment
              # inline, minus the internalBudget field.
              id

              latestContract {
                ...ApprovableContractFragment
              }

              activeContract {
                ...ApprovableContractFragment
              }

              job {
                id
                url
                currency
                name
                customFieldValues {
                  id
                  displayValue
                  customField {
                    title
                    description
                  }
                }
              }

              worker {
                id
                name
                initials
                avatar
                currency
                dayRate
                avatarPath @client
              }

              recruiter {
                id
                name
                owner {
                  id
                  name
                }
              }
              recruiterManagesWorkers
              recruiterOwnershipDays
              fees {
                id
                application
                formattedRate
              }

              status

              company {
                id
                name
              }
            }
          }
          approvalStates {
            id
            actionedBy {
              ...UserFragment
            }
            state
            cancellationReason
            message
            approver {
              id
              position
            }
            createdAt
          }
          viewerCanAction
        }
      }
    }
  `),
  {
    approvable: props.hireId,
    first: 50,
    usersFirst: 50,
    usersPage: 1,
    orderBy: [
      {
        field: ApprovalApprovableOrderByColumn.CreatedAt,
        order: SortOrder.Desc,
      },
    ],
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

// @ts-ignore It is having issues with some of the deeper fragments used on groups and states. A more in depth refactor of this computed
// ref might be warranted at some point
const approvalApprovables: ComputedRef<Approvable[]> = computed(
  () => result.value?.approvalApprovables?.data || [],
)

const reloadApprovables = () => {
  emit('reloadingApprovals')
  reloadApprovalApprovables()
}
</script>
